import { useMemo, useCallback, useState } from 'react';
import { Activity } from '../../../../../../../../api/generated/data-contracts';
import { Dropdown, IDropdownOption, IDropdownProps, IRenderFunction, Stack, TextField } from '@fluentui/react';
import { ApiCalls } from '../../../../../../../../api/api';
import { useMutation } from '@tanstack/react-query';
import { useRowMetaContext } from '../../../../context/RowMetaContext';
import { TimeEntryApprovalDtoState, useActivityState, useApprovalState, useApprovalStore, useRateState } from '../../../../../../../../_grid/grid3';
import { CUSTOM_RATE_ID } from '../../../../CONSTANTS';
import shallow from 'zustand/shallow';

const SET_NULL = 'SET_NULL';

export const ActivityDropdown = () => {
    const activities = useApprovalStore(store => Object.values(store.activities), shallow);
    const { row } = useRowMetaContext<TimeEntryApprovalDtoState>();
    const [approvalState] = useApprovalState(row.id);
    const options = useMemo(
        (): IDropdownOption<Activity | null>[] => [
            ...activities.map(activity => {
                const currencyPart = activity.rateCurrencyCode ? ` ${activity.rateCurrencyCode}` : '';
                const text = `${activity.name} (${activity.rate}${currencyPart})`;
                return {
                    key: activity.id,
                    text: text,
                    data: activity,
                };
            }),
            {
                key: SET_NULL,
                text: 'Reset',
                data: null,
            },
        ],
        [activities],
    );
    const [activity, setActivity] = useActivityState(row.id);
    const [showManualOverlay, setShowManualOverlay] = useState(false);
    const { mutate: mutateCustomRate } = useMutation((rate: number) => ApiCalls.setCustomRateForApproval({ rate, timeEntryId: row.timeEntry.id }));
    const { mutate: mutateActivity } = useMutation((selectedActivity: Activity) =>
        ApiCalls.setActivity({ selectedActivity, timeEntryId: row.timeEntry.id, approvalId: row.id }),
    );

    const [rate, setRate] = useRateState(row.id);

    const onChangeDropdown = useCallback(
        (e: any, option: IDropdownOption<Activity>) => {
            if (option) {
                const newActivity = option.data;
                const oldActivity = activity;
                if (oldActivity && newActivity && newActivity.id === oldActivity.id && newActivity.id !== CUSTOM_RATE_ID) {
                    return; // User has chosen the same non-custom activity, do nothing
                }
                if (newActivity?.id === CUSTOM_RATE_ID) {
                    setShowManualOverlay(true);
                } else {
                    mutateActivity(newActivity);
                }
                setActivity(newActivity);
                if (option.key === SET_NULL) {
                    setRate(row.originalRate);
                }
            }
        },
        [activity, mutateActivity, row.originalRate, setActivity, setRate],
    );

    const [localRateState, setLocalRateState] = useState(String(rate));

    const onRenderTitle = useCallback<IRenderFunction<IDropdownOption<Activity>[]>>(
        ([option]: [IDropdownOption<Activity>], defaultRender) => {
            if (option.data.id === CUSTOM_RATE_ID) {
                const currencyCode = row.originalRateCurrencyCode || '';
                const manualText = `Manually entered: ${rate} ${currencyCode}`.trim();
                return defaultRender?.([{ ...option, text: manualText }]);
            }
            return defaultRender?.([option]);
        },
        [rate, row.originalRateCurrencyCode],
    );

    const onBlur = useCallback(() => {
        const newRate = Number(localRateState);
        if (!Number.isNaN(newRate)) {
            setShowManualOverlay(false);
            mutateCustomRate(newRate);
            setRate(newRate);
        } else {
            setLocalRateState(String(rate));
        }
    }, [localRateState, mutateCustomRate, rate, setRate]);

    return (
        <Stack horizontal>
            {showManualOverlay ? (
                <TextField
                    autoFocus
                    styles={{ root: { width: 200 } }}
                    type="number"
                    onChange={(e, newValue) => {
                        setLocalRateState(newValue);
                    }}
                    // value={String(localRateState)}
                    defaultValue={String(localRateState)}
                    onBlur={onBlur}
                    onKeyDown={e => {
                        if (e.key === 'Enter') {
                            (e.target as HTMLDivElement).blur();
                        }
                    }}
                />
            ) : (
                <Dropdown
                    styles={dropdownStyles}
                    options={options}
                    selectedKey={activity?.id || null}
                    onChange={onChangeDropdown}
                    onRenderTitle={onRenderTitle}
                    placeholder="Select rate"
                    disabled={approvalState === 'Approved'}
                />
            )}
        </Stack>
    );
};

const dropdownStyles: IDropdownProps['styles'] = { root: { width: 200 }, callout: { minWidth: 'min-content' } };
